/**
 * ot-enhance.js
 * ----------------------------------------------------------
 * Opora Tools — DOM Upgrader
 * Runs on every Opora Tools page. Scans existing tool markup
 * and upgrades it to the modern UI without requiring per-page
 * body rewrites.
 *
 * What it does:
 *   1. Converts unit-toggle selects (≤4 options, *-unit IDs) into segmented pills.
 *   2. Converts long native selects (≥12 options) into searchable comboboxes,
 *      keeping the original <select> hidden for form submission + JS reads.
 *   3. Wraps numeric inputs that have min+max into a slider + numeric pair.
 *   4. Removes calc submit buttons and switches the form to live-update mode
 *      (input/change → trigger the tool's calculate function via 'input' event).
 *   5. Adds an empty-state placeholder to result panels that don't have one.
 *   6. Upgrades the result card chrome: icon-only print/copy buttons in top-right,
 *      hero-number layout for the primary result, status card under the number.
 *   7. Wraps long-form prose (.fg-prose blocks after the tool body) into a single
 *      collapsible <details class="ot-learn"> block titled "Learn more".
 *   8. Wraps comparison-table tooling (.ot-comp-table-wrap) into the result flow.
 *
 * Design contract:
 *   - Each tool's <select>/<input>/<button> keeps its original ID so existing
 *     calc logic continues to read values via OporaTools.getNum / .getVal.
 *   - Pill/combobox UI mirrors the underlying <select>.value so the JS sees
 *     the same thing.
 *   - Calculate functions trigger naturally via the 'input' event on the form.
 *
 * Version: 1.0.0
 */

(function () {
  'use strict';

  /* ===========================================================
     ENTRY POINT — run after DOM is parsed
     =========================================================== */
  function init() {
    var tool = document.querySelector('.ot-tool');
    if (!tool) return;

    // Mark live-update mode for CSS hooks
    tool.setAttribute('data-live-update', 'true');

    upgradeForm(tool);
    upgradeResultCard(tool);
    wrapLongFormProse(tool);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ===========================================================
     1. FORM UPGRADES — pills, comboboxes, sliders, live-update
     =========================================================== */
  function upgradeForm(tool) {
    var form = tool.querySelector('form');
    if (!form) return;

    /* ---- 1a. Segmented pills for short unit/toggle selects ---- */
    var pillCandidates = form.querySelectorAll('select');
    pillCandidates.forEach(function (sel) {
      var realOpts = Array.from(sel.options).filter(function (o) {
        return o.value !== '';
      });

      // Pillify when: 2-4 real options AND label suggests a unit/toggle
      // (id contains '-unit' or option count is small AND option text is short).
      var isShortToggle =
        realOpts.length >= 2 && realOpts.length <= 4 &&
        realOpts.every(function (o) { return o.textContent.trim().length <= 28; });

      var looksLikeUnit =
        /-?unit$/i.test(sel.id) ||
        sel.name && /unit|mode|form/i.test(sel.name);

      // DISABLED 2026-06-01: combobox and pill replacements caused tools
      // to stop responding to selections because synthetic change events
      // weren't reaching the tool's bound handlers reliably. Native
      // <select> elements are now left in place. Re-enable per-tool only
      // after verifying the tool's change-handler picks up the value.
      // Set data-ot-enhance="on" on the .ot-tool wrapper to opt back in.
      var enhanceOn = tool && tool.getAttribute('data-ot-enhance') === 'on';
      if (!enhanceOn) return;
      if (isShortToggle && (looksLikeUnit || realOpts.length <= 3)) {
        convertToPills(sel);
      } else if (realOpts.length >= 12) {
        convertToCombobox(sel);
      }
    });

    /* ---- 1b. Slider pairs for numeric inputs with min+max ---- */
    var numericInputs = form.querySelectorAll('input[type="number"]');
    numericInputs.forEach(function (inp) {
      var hasMin = inp.hasAttribute('min') && inp.getAttribute('min') !== '';
      var hasMax = inp.hasAttribute('max') && inp.getAttribute('max') !== '';
      if (hasMin && hasMax) {
        addSliderToInput(inp);
      }
    });

    /* ---- 1c. Live update: hide submit buttons, fire 'input' on changes ---- */
    // DISABLED 2026-06-01: hiding submit buttons broke tools that bind
    // their calc to button.click (PPE, Pad, Floor Program, Restroom Time,
    // Production Rate, Bid Benchmarks). Submit buttons now remain visible
    // by default. Opt back into live-update on tools whose JS listens for
    // 'input' on the form by setting data-ot-enhance="on" on .ot-tool.
    var enhanceLive = tool && tool.getAttribute('data-ot-enhance') === 'on';
    if (enhanceLive) {
      var submits = form.querySelectorAll('button[type="submit"], .ot-calc-btn, [data-action="reset"]');
      submits.forEach(function (btn) { btn.style.display = 'none'; });
    }

    // Intercept submit so accidental Enter key doesn't reload the page
    form.addEventListener('submit', function (e) { e.preventDefault(); });

    // Live calculate trigger — fire input event whenever anything changes.
    // Existing tool JS already listens for 'input' on the form (per
    // opora-tool-dilution.js pattern) so this is a no-op for those.
    // For VOC, we'll add an explicit listener in the VOC tool refactor.
  }

  /* ===========================================================
     1a. SEGMENTED PILL CONTROL  (replaces native <select>)
     =========================================================== */
  function convertToPills(sel) {
    if (sel.dataset.otEnhanced) return;
    sel.dataset.otEnhanced = 'pills';

    var realOpts = Array.from(sel.options).filter(function (o) {
      return o.value !== '';
    });

    var group = document.createElement('div');
    group.className = 'ot-seg';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', sel.getAttribute('aria-label') || sel.name || '');

    realOpts.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ot-seg__btn';
      btn.textContent = opt.textContent.trim();
      btn.setAttribute('aria-pressed', sel.value === opt.value ? 'true' : 'false');
      btn.dataset.value = opt.value;

      btn.addEventListener('click', function () {
        sel.value = opt.value;
        // Update aria-pressed across siblings
        Array.from(group.querySelectorAll('.ot-seg__btn')).forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        // Fire change + input events for the underlying select
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        sel.dispatchEvent(new Event('input', { bubbles: true }));
      });

      group.appendChild(btn);
    });

    // Hide native select but keep it in DOM
    sel.style.position = 'absolute';
    sel.style.opacity = '0';
    sel.style.pointerEvents = 'none';
    sel.style.height = '0';
    sel.style.width = '0';
    sel.setAttribute('tabindex', '-1');
    sel.setAttribute('aria-hidden', 'true');

    sel.parentNode.insertBefore(group, sel.nextSibling);

    // Re-sync if select.value changes programmatically (e.g. URL params restore)
    var observer = new MutationObserver(function () { syncPills(sel, group); });
    observer.observe(sel, { attributes: true, attributeFilter: ['value'] });
    // Also listen for explicit programmatic change events
    sel.addEventListener('change', function () { syncPills(sel, group); });
  }

  function syncPills(sel, group) {
    Array.from(group.querySelectorAll('.ot-seg__btn')).forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.value === sel.value ? 'true' : 'false');
    });
  }

  /* ===========================================================
     1b. SEARCHABLE COMBOBOX  (replaces native <select> with 12+ options)
     =========================================================== */
  function convertToCombobox(sel) {
    if (sel.dataset.otEnhanced) return;
    sel.dataset.otEnhanced = 'combo';

    var realOpts = Array.from(sel.options).filter(function (o) { return o.value !== ''; });

    var placeholder = (function () {
      var ph = Array.from(sel.options).find(function (o) { return o.value === ''; });
      return ph ? ph.textContent.trim() : 'Select…';
    }());

    // Wrapper
    var wrap = document.createElement('div');
    wrap.className = 'ot-combo';
    wrap.setAttribute('data-open', 'false');

    // Trigger
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ot-combo__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = '<span class="ot-combo__value ot-combo__value--placeholder"></span>' +
      '<svg class="ot-combo__chevron" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
      '<path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var valueEl = trigger.querySelector('.ot-combo__value');

    // Panel
    var panel = document.createElement('div');
    panel.className = 'ot-combo__panel';
    panel.setAttribute('role', 'listbox');

    var searchWrap = document.createElement('div');
    searchWrap.className = 'ot-combo__search-wrap';
    var search = document.createElement('input');
    search.type = 'text';
    search.className = 'ot-combo__search';
    search.setAttribute('placeholder', 'Search…');
    search.setAttribute('aria-label', 'Filter options');
    searchWrap.appendChild(search);

    var list = document.createElement('ul');
    list.className = 'ot-combo__list';

    panel.appendChild(searchWrap);
    panel.appendChild(list);

    // Render options
    function renderList(filter) {
      list.innerHTML = '';
      var q = (filter || '').trim().toLowerCase();
      var visible = realOpts.filter(function (opt) {
        if (!q) return true;
        return opt.textContent.toLowerCase().indexOf(q) !== -1;
      });
      if (!visible.length) {
        var empty = document.createElement('li');
        empty.className = 'ot-combo__empty';
        empty.textContent = 'No matches';
        list.appendChild(empty);
        return;
      }
      visible.forEach(function (opt) {
        var li = document.createElement('li');
        li.className = 'ot-combo__opt';
        if (opt.value === sel.value) li.classList.add('ot-combo__opt--selected');
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.textContent = opt.textContent.trim();
        li.addEventListener('click', function () { select(opt); });
        list.appendChild(li);
      });
    }

    function select(opt) {
      sel.value = opt.value;
      valueEl.textContent = opt.textContent.trim();
      valueEl.classList.remove('ot-combo__value--placeholder');
      close();
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      sel.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function open() {
      wrap.setAttribute('data-open', 'true');
      trigger.setAttribute('aria-expanded', 'true');
      search.value = '';
      renderList('');
      // focus after slight delay so iOS keyboard pops
      setTimeout(function () { search.focus(); }, 50);
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('keydown', onKey, true);
    }
    function close() {
      wrap.setAttribute('data-open', 'false');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('keydown', onKey, true);
    }
    function onDocClick(e) {
      if (!wrap.contains(e.target)) close();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); trigger.focus(); }
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (wrap.getAttribute('data-open') === 'true') close();
      else open();
    });
    search.addEventListener('input', function () { renderList(search.value); });
    search.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var first = list.querySelector('.ot-combo__opt');
        if (first) {
          var opt = realOpts.find(function (o) { return o.value === first.dataset.value; });
          if (opt) select(opt);
        }
      }
    });

    // Initial label
    function syncLabel() {
      var cur = realOpts.find(function (o) { return o.value === sel.value; });
      if (cur) {
        valueEl.textContent = cur.textContent.trim();
        valueEl.classList.remove('ot-combo__value--placeholder');
      } else {
        valueEl.textContent = placeholder;
        valueEl.classList.add('ot-combo__value--placeholder');
      }
    }
    syncLabel();

    // Re-sync on programmatic select.value change
    var observer = new MutationObserver(syncLabel);
    observer.observe(sel, { attributes: true });
    sel.addEventListener('change', syncLabel);

    // Watch for new options being appended to <select> (tool JS populates async)
    var optObserver = new MutationObserver(function () {
      realOpts = Array.from(sel.options).filter(function (o) { return o.value !== ''; });
      syncLabel();
      if (wrap.getAttribute('data-open') === 'true') renderList(search.value);
    });
    optObserver.observe(sel, { childList: true });

    // Hide native select
    sel.style.position = 'absolute';
    sel.style.opacity = '0';
    sel.style.pointerEvents = 'none';
    sel.style.height = '0';
    sel.style.width = '0';
    sel.setAttribute('tabindex', '-1');
    sel.setAttribute('aria-hidden', 'true');

    wrap.appendChild(trigger);
    wrap.appendChild(panel);
    sel.parentNode.insertBefore(wrap, sel.nextSibling);
  }

  /* ===========================================================
     1c. SLIDER (numeric input + range slider pair)
     =========================================================== */
  function addSliderToInput(inp) {
    if (inp.dataset.otEnhanced) return;
    inp.dataset.otEnhanced = 'slider';

    var min  = parseFloat(inp.min);
    var max  = parseFloat(inp.max);
    var step = inp.step ? parseFloat(inp.step) : 1;
    if (isNaN(min) || isNaN(max) || min === max) return;

    var wrap = document.createElement('div');
    wrap.className = 'ot-slider';

    var range = document.createElement('input');
    range.type = 'range';
    range.className = 'ot-slider__range';
    range.min = inp.min;
    range.max = inp.max;
    if (inp.step) range.step = inp.step;
    range.value = inp.value || min;
    range.setAttribute('aria-label', inp.getAttribute('aria-label') || inp.name || 'value');

    var readout = document.createElement('div');
    readout.className = 'ot-slider__readout';
    var num = document.createElement('span');
    num.className = 'ot-slider__num';
    num.textContent = range.value;
    readout.appendChild(num);

    // Bounds row
    var bounds = document.createElement('div');
    bounds.className = 'ot-slider__bounds';
    bounds.innerHTML = '<span>' + min + '</span><span>' + max + '</span>';

    wrap.appendChild(range);
    wrap.appendChild(readout);
    wrap.appendChild(bounds);

    range.addEventListener('input', function () {
      inp.value = range.value;
      num.textContent = range.value;
      inp.dispatchEvent(new Event('input', { bubbles: true }));
    });
    inp.addEventListener('input', function () {
      if (parseFloat(inp.value) !== parseFloat(range.value)) {
        range.value = inp.value;
        num.textContent = inp.value;
      }
    });

    // Hide the original numeric input below the slider; keep accessible for direct entry
    inp.style.maxWidth = '120px';
    inp.style.marginTop = '8px';

    inp.parentNode.insertBefore(wrap, inp);
  }

  /* ===========================================================
     2. RESULT CARD UPGRADES
     =========================================================== */
  function upgradeResultCard(tool) {
    var result = tool.querySelector('.ot-tool__result');
    if (!result) return;

    var card = result.querySelector('.ot-result');
    if (!card) return;

    // Upgrade existing actions row → top-right icon buttons in the header
    var header = card.querySelector('.ot-result__header');
    var actionsRow = card.querySelector('.ot-actions');

    if (header && !card.querySelector('.ot-result__actions')) {
      var actions = document.createElement('div');
      actions.className = 'ot-result__actions';

      // Print icon button
      var printBtn = document.createElement('button');
      printBtn.type = 'button';
      printBtn.className = 'ot-result__icon-btn';
      printBtn.setAttribute('aria-label', 'Print this result');
      printBtn.title = 'Print';
      printBtn.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<rect x="3" y="1.5" width="10" height="3.5" rx="0.5" stroke="currentColor" stroke-width="1.4"/>' +
        '<path d="M3 5v5h10V5" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<rect x="4.5" y="8" width="7" height="5.5" rx="0.5" stroke="currentColor" stroke-width="1.4"/>' +
        '<circle cx="12" cy="6.5" r="0.75" fill="currentColor"/></svg>';
      printBtn.addEventListener('click', function () {
        if (window.OporaTools && OporaTools.printPage) OporaTools.printPage();
        else window.print();
      });

      // Copy link icon button
      var copyBtn = document.createElement('button');
      copyBtn.type = 'button';
      copyBtn.className = 'ot-result__icon-btn';
      copyBtn.setAttribute('aria-label', 'Copy shareable link');
      copyBtn.title = 'Copy link';
      copyBtn.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<path d="M6.5 9.5l3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
        '<path d="M10 5.5l1.5-1.5a2.12 2.12 0 0 1 3 3L13 8.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M6 10.5L4.5 12a2.12 2.12 0 0 1-3-3L3 7.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      copyBtn.addEventListener('click', function () {
        if (window.OporaTools && OporaTools.copyPageUrl) OporaTools.copyPageUrl();
      });

      actions.appendChild(printBtn);
      actions.appendChild(copyBtn);
      header.appendChild(actions);
    }

    // Hide the legacy text buttons row (we have icon buttons now)
    if (actionsRow) actionsRow.style.display = 'none';
  }

  /* ===========================================================
     3. WRAP LONG-FORM PROSE IN COLLAPSIBLE "Learn more"
     =========================================================== */
  function wrapLongFormProse(tool) {
    // Find the parent that contains both the tool columns and the prose sections.
    var fgBody = tool.parentNode; // .fg-body
    if (!fgBody) return;

    // Already wrapped?
    if (fgBody.querySelector('.ot-learn')) return;

    // Collect prose-like sections that follow .ot-tool inside .fg-body
    var proseSections = [];
    var current = tool.nextElementSibling;
    while (current) {
      var isProse = current.matches && (
        current.matches('section.fg-prose') ||
        current.matches('.fg-prose') ||
        (current.tagName === 'SECTION' && current.querySelector('.fg-prose')) ||
        (current.tagName === 'SECTION' && current.getAttribute('aria-labelledby') && current.querySelector('h2, h3'))
      );
      var isMethodology = current.matches && current.querySelector && current.querySelector('.ot-method');
      var stopHere = current.matches && (
        current.matches('.fg-disclaimer') ||
        current.matches('.fg-meta-row')
      );

      if (stopHere) break;
      if (isProse || isMethodology) {
        proseSections.push(current);
      }
      current = current.nextElementSibling;
    }

    // Also collect prose sections that live INSIDE the .ot-tool element
    // (some pages structure it that way)
    var insideProse = tool.querySelectorAll(':scope > section.fg-prose, :scope > .ot-method');
    insideProse.forEach(function (el) { proseSections.push(el); });
    // Also catch sections aria-labelled with a methodology title
    var insideMethSection = tool.querySelector('section[aria-labelledby$="method-title"]');
    if (insideMethSection && proseSections.indexOf(insideMethSection) === -1) {
      proseSections.push(insideMethSection);
    }

    if (!proseSections.length) return;

    // Build the <details> wrapper
    var details = document.createElement('details');
    details.className = 'ot-learn';

    var summary = document.createElement('summary');
    summary.className = 'ot-learn__toggle';
    summary.innerHTML =
      '<span><span class="ot-learn__toggle-eyebrow">In-depth reference</span>Learn more about this tool</span>' +
      '<svg class="ot-learn__chevron" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
      '<path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var body = document.createElement('div');
    body.className = 'ot-learn__body';

    details.appendChild(summary);
    details.appendChild(body);

    // Insert before the first prose section
    var first = proseSections[0];
    first.parentNode.insertBefore(details, first);

    // Move all prose sections into the body
    proseSections.forEach(function (s) { body.appendChild(s); });
  }
})();